import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import {
  listCompetencyCategories,
  createCompetencyCategory,
  updateCompetencyCategory,
  deleteCompetencyCategory,
  listCompetencies,
  createCompetency,
  updateCompetency,
  deleteCompetency,
  type CompetencyCategoryDto,
  type CompetencyDto,
} from '../../lib/competencies';
import {
  BookOpen,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  ChevronRight,
  Layers,
  Info,
} from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/kinerja/competencies')({
  component: CompetenciesPage,
});

function CompetenciesPage() {
  return (
    <ProtectedRoute minimumRole="hr">
      <CompetenciesContent />
    </ProtectedRoute>
  );
}

// Fixed 5-level map — keys "1"–"5"
const LEVELS = ['1', '2', '3', '4', '5'] as const;
const LEVEL_LABELS: Record<string, string> = {
  '1': 'Level 1 — Awareness',
  '2': 'Level 2 — Developing',
  '3': 'Level 3 — Proficient',
  '4': 'Level 4 — Advanced',
  '5': 'Level 5 — Expert',
};

function emptyLevelMap(): Record<string, string> {
  return { '1': '', '2': '', '3': '', '4': '', '5': '' };
}

function CompetenciesContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;

  // ── Categories ─────────────────────────────────────────────────────────────
  const [categories, setCategories] = useState<CompetencyCategoryDto[]>([]);
  const [catsLoading, setCatsLoading] = useState(true);
  const [selectedCatId, setSelectedCatId] = useState<string | null>(null);

  // create category
  const [addingCat, setAddingCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatDesc, setNewCatDesc] = useState('');
  const [catSaving, setCatSaving] = useState(false);

  // edit category inline
  const [editCatId, setEditCatId] = useState<string | null>(null);
  const [editCatName, setEditCatName] = useState('');

  // ── Competencies ───────────────────────────────────────────────────────────
  const [competencies, setCompetencies] = useState<CompetencyDto[]>([]);
  const [compsLoading, setCompsLoading] = useState(false);

  // create competency
  const [addingComp, setAddingComp] = useState(false);
  const [newComp, setNewComp] = useState({
    code: '',
    name: '',
    description: '',
    levelMap: emptyLevelMap(),
  });
  const [compSaving, setCompSaving] = useState(false);
  const [compError, setCompError] = useState('');

  // edit competency inline
  const [editCompId, setEditCompId] = useState<string | null>(null);
  const [editComp, setEditComp] = useState({
    code: '',
    name: '',
    description: '',
    levelMap: emptyLevelMap(),
  });
  const [editCompSaving, setEditCompSaving] = useState(false);

  // expanded levelMap view
  const [expandedCompId, setExpandedCompId] = useState<string | null>(null);

  // ── Load categories ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    listCompetencyCategories(companyId).then((res) => {
      if (cancelled) return;
      setCatsLoading(false);
      if (res.success && res.data) {
        setCategories(res.data.categories);
        if (res.data.categories.length > 0 && !selectedCatId) {
          setSelectedCatId(res.data.categories[0].id);
        }
      }
    });
    return () => { cancelled = true; };
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load competencies when category selected ────────────────────────────────
  const loadCompetencies = useCallback(
    (catId: string) => {
      let cancelled = false;
      setCompsLoading(true);
      setCompetencies([]);
      listCompetencies({ companyId, categoryId: catId }).then((res) => {
        if (cancelled) return;
        setCompsLoading(false);
        if (res.success && res.data) setCompetencies(res.data.competencies);
      });
      return () => { cancelled = true; };
    },
    [companyId],
  );

  useEffect(() => {
    if (!selectedCatId) return;
    const cleanup = loadCompetencies(selectedCatId);
    return cleanup;
  }, [selectedCatId, loadCompetencies]);

  // ── Category actions ────────────────────────────────────────────────────────
  async function handleCreateCategory(e: React.FormEvent) {
    e.preventDefault();
    if (!newCatName.trim()) return;
    setCatSaving(true);
    const res = await createCompetencyCategory({
      companyId,
      name: newCatName.trim(),
      description: newCatDesc.trim() || undefined,
    });
    setCatSaving(false);
    if (res.success && res.data) {
      setCategories((prev) => [...prev, res.data!.category]);
      setSelectedCatId(res.data.category.id);
      setNewCatName('');
      setNewCatDesc('');
      setAddingCat(false);
    }
  }

  async function handleUpdateCategory(cat: CompetencyCategoryDto) {
    if (!editCatName.trim()) return;
    const res = await updateCompetencyCategory(cat.id, {
      companyId,
      name: editCatName.trim(),
      description: cat.description,
    });
    if (res.success && res.data) {
      setCategories((prev) => prev.map((c) => (c.id === cat.id ? res.data!.category : c)));
      setEditCatId(null);
    }
  }

  async function handleDeleteCategory(catId: string) {
    const res = await deleteCompetencyCategory(catId, companyId);
    if (res.success) {
      setCategories((prev) => prev.filter((c) => c.id !== catId));
      if (selectedCatId === catId) {
        const remaining = categories.filter((c) => c.id !== catId);
        setSelectedCatId(remaining.length > 0 ? remaining[0].id : null);
      }
    }
  }

  // ── Competency actions ──────────────────────────────────────────────────────
  async function handleCreateCompetency(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedCatId || !newComp.code.trim() || !newComp.name.trim()) return;
    setCompSaving(true);
    setCompError('');
    const res = await createCompetency({
      companyId,
      categoryId: selectedCatId,
      code: newComp.code.trim().toUpperCase(),
      name: newComp.name.trim(),
      description: newComp.description.trim() || undefined,
      levelMap: newComp.levelMap,
    });
    setCompSaving(false);
    if (res.success && res.data) {
      setCompetencies((prev) => [...prev, res.data!.competency]);
      setNewComp({ code: '', name: '', description: '', levelMap: emptyLevelMap() });
      setAddingComp(false);
    } else {
      setCompError(res.error ?? 'Failed to create competency');
    }
  }

  function startEditComp(comp: CompetencyDto) {
    const lm = { ...emptyLevelMap(), ...comp.levelMap };
    setEditCompId(comp.id);
    setEditComp({
      code: comp.code,
      name: comp.name,
      description: comp.description ?? '',
      levelMap: lm,
    });
  }

  async function handleUpdateCompetency(comp: CompetencyDto) {
    setEditCompSaving(true);
    const res = await updateCompetency(comp.id, {
      companyId,
      code: editComp.code.trim().toUpperCase(),
      name: editComp.name.trim(),
      description: editComp.description.trim() || undefined,
      levelMap: editComp.levelMap,
    });
    setEditCompSaving(false);
    if (res.success && res.data) {
      setCompetencies((prev) => prev.map((c) => (c.id === comp.id ? res.data!.competency : c)));
      setEditCompId(null);
    }
  }

  async function handleDeleteCompetency(compId: string) {
    const res = await deleteCompetency(compId, companyId);
    if (res.success) {
      setCompetencies((prev) => prev.filter((c) => c.id !== compId));
    }
  }

  const selectedCat = categories.find((c) => c.id === selectedCatId) ?? null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-brand-50 shrink-0">
          <BookOpen className="w-5 h-5 text-brand-500" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-foreground tracking-tight">Competency Dictionary</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            The benchmark all employee development plans are measured against
          </p>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2.5 rounded-lg border border-brand-100 bg-brand-50/50 px-4 py-3">
        <Info className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
        <p className="text-xs text-brand-500 leading-relaxed">
          Each competency defines behavioral indicators at 5 levels (1 = Awareness, 5 = Expert).
          Position profiles use these levels to set their required standard.
          IDP generation compares employee performance against these benchmarks.
        </p>
      </div>

      {/* Two-panel layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:h-[calc(100vh-280px)]">

        {/* Left: Category list */}
        <Card className="md:col-span-1 flex flex-col overflow-hidden">
          <CardHeader className="py-3 px-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Categories</CardTitle>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={() => setAddingCat(true)}
              >
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
          </CardHeader>
          <div className="flex-1 overflow-y-auto">
            {catsLoading ? (
              <div className="p-3 space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 bg-muted rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="py-1">
                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className={cn(
                      'group flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-accent/50 transition-colors',
                      selectedCatId === cat.id && 'bg-accent',
                    )}
                    onClick={() => {
                      setSelectedCatId(cat.id);
                      setEditCatId(null);
                      setAddingComp(false);
                      setEditCompId(null);
                    }}
                  >
                    {editCatId === cat.id ? (
                      <div className="flex items-center gap-1.5 flex-1" onClick={(e) => e.stopPropagation()}>
                        <Input
                          className="h-7 text-xs flex-1"
                          value={editCatName}
                          onChange={(e) => setEditCatName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleUpdateCategory(cat);
                            if (e.key === 'Escape') setEditCatId(null);
                          }}
                          autoFocus
                        />
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 shrink-0" onClick={() => handleUpdateCategory(cat)}>
                          <Check className="w-3 h-3 text-emerald-600" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 shrink-0" onClick={() => setEditCatId(null)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Layers className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="text-sm flex-1 truncate">{cat.name}</span>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditCatId(cat.id);
                              setEditCatName(cat.name);
                            }}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCategory(cat.id);
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        {selectedCatId === cat.id && (
                          <ChevronRight className="w-3.5 h-3.5 text-primary shrink-0" />
                        )}
                      </>
                    )}
                  </div>
                ))}

                {/* Inline add category form */}
                {addingCat && (
                  <form
                    onSubmit={handleCreateCategory}
                    className="px-3 py-2.5 space-y-2 border-t"
                  >
                    <Input
                      className="h-7 text-xs"
                      placeholder="Category name"
                      value={newCatName}
                      onChange={(e) => setNewCatName(e.target.value)}
                      autoFocus
                    />
                    <Input
                      className="h-7 text-xs"
                      placeholder="Description (optional)"
                      value={newCatDesc}
                      onChange={(e) => setNewCatDesc(e.target.value)}
                    />
                    <div className="flex gap-1.5">
                      <Button type="submit" size="sm" className="h-7 text-xs flex-1" disabled={catSaving}>
                        {catSaving ? 'Saving…' : 'Add'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => { setAddingCat(false); setNewCatName(''); setNewCatDesc(''); }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}

                {categories.length === 0 && !addingCat && (
                  <div className="p-6 text-center">
                    <p className="text-xs text-muted-foreground">No categories yet.</p>
                    <button
                      onClick={() => setAddingCat(true)}
                      className="text-xs text-primary hover:underline mt-1"
                    >
                      Add the first category
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Right: Competency list */}
        <Card className="md:col-span-2 flex flex-col overflow-hidden">
          <CardHeader className="py-3 px-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">
                {selectedCat ? selectedCat.name : 'Competencies'}
                {selectedCat?.description && (
                  <span className="text-xs font-normal text-muted-foreground ml-2">
                    — {selectedCat.description}
                  </span>
                )}
              </CardTitle>
              {selectedCatId && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1 text-xs"
                  onClick={() => {
                    setAddingComp(true);
                    setEditCompId(null);
                  }}
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Competency
                </Button>
              )}
            </div>
          </CardHeader>

          <div className="flex-1 overflow-y-auto">
            {!selectedCatId ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <BookOpen className="w-10 h-10 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">Select a category to view its competencies</p>
              </div>
            ) : compsLoading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="divide-y">
                {/* Add competency form */}
                {addingComp && (
                  <form onSubmit={handleCreateCompetency} className="p-4 space-y-3 bg-accent/30">
                    <p className="text-xs font-medium text-foreground">New Competency</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Code</Label>
                        <Input
                          className="h-8 text-xs uppercase"
                          placeholder="e.g. LEADERSHIP"
                          value={newComp.code}
                          onChange={(e) => setNewComp((f) => ({ ...f, code: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Name</Label>
                        <Input
                          className="h-8 text-xs"
                          placeholder="e.g. Leadership"
                          value={newComp.name}
                          onChange={(e) => setNewComp((f) => ({ ...f, name: e.target.value }))}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Description</Label>
                      <Input
                        className="h-8 text-xs"
                        placeholder="What this competency covers"
                        value={newComp.description}
                        onChange={(e) => setNewComp((f) => ({ ...f, description: e.target.value }))}
                      />
                    </div>
                    <Separator />
                    <p className="text-xs font-medium text-foreground">Behavioral Indicators (per level)</p>
                    {LEVELS.map((lvl) => (
                      <div key={lvl} className="space-y-1">
                        <Label className="text-xs text-muted-foreground">{LEVEL_LABELS[lvl]}</Label>
                        <Input
                          className="h-8 text-xs"
                          placeholder={`Describe behavior at level ${lvl}…`}
                          value={newComp.levelMap[lvl] ?? ''}
                          onChange={(e) =>
                            setNewComp((f) => ({
                              ...f,
                              levelMap: { ...f.levelMap, [lvl]: e.target.value },
                            }))
                          }
                        />
                      </div>
                    ))}
                    {compError && <p className="text-xs text-destructive">{compError}</p>}
                    <div className="flex gap-2">
                      <Button type="submit" size="sm" className="text-xs" disabled={compSaving}>
                        {compSaving ? 'Saving…' : 'Create'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-xs"
                        onClick={() => {
                          setAddingComp(false);
                          setNewComp({ code: '', name: '', description: '', levelMap: emptyLevelMap() });
                          setCompError('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}

                {/* Competency rows */}
                {competencies.length === 0 && !addingComp ? (
                  <div className="p-8 text-center">
                    <p className="text-sm text-muted-foreground">No competencies in this category yet.</p>
                    <button
                      onClick={() => setAddingComp(true)}
                      className="text-xs text-primary hover:underline mt-1"
                    >
                      Add the first competency
                    </button>
                  </div>
                ) : (
                  competencies.map((comp) => (
                    <div key={comp.id} className="p-4">
                      {editCompId === comp.id ? (
                        /* Edit form */
                        <div className="space-y-3">
                          <p className="text-xs font-medium text-foreground">Edit Competency</p>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Code</Label>
                              <Input
                                className="h-8 text-xs uppercase"
                                value={editComp.code}
                                onChange={(e) => setEditComp((f) => ({ ...f, code: e.target.value }))}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Name</Label>
                              <Input
                                className="h-8 text-xs"
                                value={editComp.name}
                                onChange={(e) => setEditComp((f) => ({ ...f, name: e.target.value }))}
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Description</Label>
                            <Input
                              className="h-8 text-xs"
                              value={editComp.description}
                              onChange={(e) => setEditComp((f) => ({ ...f, description: e.target.value }))}
                            />
                          </div>
                          <Separator />
                          <p className="text-xs font-medium text-foreground">Behavioral Indicators (per level)</p>
                          {LEVELS.map((lvl) => (
                            <div key={lvl} className="space-y-1">
                              <Label className="text-xs text-muted-foreground">{LEVEL_LABELS[lvl]}</Label>
                              <Input
                                className="h-8 text-xs"
                                placeholder={`Describe behavior at level ${lvl}…`}
                                value={editComp.levelMap[lvl] ?? ''}
                                onChange={(e) =>
                                  setEditComp((f) => ({
                                    ...f,
                                    levelMap: { ...f.levelMap, [lvl]: e.target.value },
                                  }))
                                }
                              />
                            </div>
                          ))}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="text-xs"
                              disabled={editCompSaving}
                              onClick={() => handleUpdateCompetency(comp)}
                            >
                              {editCompSaving ? 'Saving…' : 'Save'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-xs"
                              onClick={() => setEditCompId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* View row */
                        <div>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2.5 flex-1 min-w-0">
                              <Badge variant="secondary" className="text-xs font-mono shrink-0 mt-0.5">
                                {comp.code}
                              </Badge>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground">{comp.name}</p>
                                {comp.description && (
                                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{comp.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors px-1.5 py-1 rounded hover:bg-accent"
                                onClick={() =>
                                  setExpandedCompId(expandedCompId === comp.id ? null : comp.id)
                                }
                              >
                                {expandedCompId === comp.id ? 'Hide levels' : 'View levels'}
                              </button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => startEditComp(comp)}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                onClick={() => handleDeleteCompetency(comp.id)}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>

                          {/* Expanded level map */}
                          {expandedCompId === comp.id && (
                            <div className="mt-3 grid grid-cols-1 gap-1.5 pl-2 border-l-2 border-brand-100">
                              {LEVELS.map((lvl) => {
                                const desc = comp.levelMap?.[lvl];
                                return (
                                  <div key={lvl} className="flex items-start gap-2">
                                    <span className="text-xs font-semibold text-brand-500 w-14 shrink-0 mt-0.5">
                                      Lvl {lvl}
                                    </span>
                                    <span className={cn(
                                      'text-xs',
                                      desc ? 'text-foreground' : 'text-muted-foreground italic',
                                    )}>
                                      {desc || 'No description defined'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
